import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import RiskAssessmentModal from './RiskAssessmentModal';
import DocumentGeneratorModal from './DocumentGeneratorModal';

interface OnboardingTask {
  taskId: string;
  taskName: string;
  taskCategory: 'CONSULTATION' | 'ASSESSMENT' | 'DOCUMENTATION' | 'APPLICATION' | 'TRANSFER' | 'FUNDING';
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'BLOCKED' | 'SKIPPED';
  description?: string;
  isRequired: boolean;
  dueDate?: string;
  completedAt?: string;
  completedBy?: string;
  blockedReason?: string;
}

interface OnboardingClient {
  onboardingId: string;
  householdName: string;
  primaryContactName: string;
  primaryEmail: string;
  primaryPhone: string;
  stage: 'PROSPECT' | 'DOCUMENTS' | 'ACCOUNTS' | 'FUNDING' | 'COMPLETE';
  progressPercent: number;
  tasks: OnboardingTask[];
  createdAt: string;
  startedAt: string;
  completedAt?: string;
  lastActivityAt: string;
  assignedAdvisor?: { username: string; fullName?: string };
  estimatedAum: number;
  riskProfile?: string;
  investmentObjective?: string;
  notes?: string;
}

interface OnboardingStats {
  prospectCount: number;
  documentsCount: number;
  accountsCount: number;
  fundingCount: number;
  completeCount: number;
  totalActive: number;
  overdueTasksCount: number;
  blockedTasksCount: number;
}

const Container = styled.div<{ embedded?: boolean }>`
  padding: ${props => props.embedded ? '1.5rem' : '2rem'};
  background: ${props => props.embedded ? 'transparent' : 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'};
  min-height: ${props => props.embedded ? 'auto' : 'calc(100vh - 64px)'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    }
  ` : props.variant === 'danger' ? `
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
    &:hover {
      background: rgba(239, 68, 68, 0.3);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
`;

const Pipeline = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
`;

const PipelineStage = styled.div<{ active?: boolean }>`
  background: ${props => props.active ? 'rgba(59, 130, 246, 0.1)' : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${props => props.active ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const StageLabel = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
`;

const StageCount = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
`;

const ClientList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ClientCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;

const ClientHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ClientInfo = styled.div``;

const ClientName = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 0.25rem 0;
`;

const ClientContact = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
`;

const ClientMeta = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const StageBadge = styled.span<{ stage: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    switch (props.stage) {
      case 'COMPLETE':
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
      case 'FUNDING':
        return 'background: rgba(59, 130, 246, 0.2); color: #3b82f6;';
      case 'ACCOUNTS':
        return 'background: rgba(168, 85, 247, 0.2); color: #a855f7;';
      case 'DOCUMENTS':
        return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
      case 'PROSPECT':
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const ProgressBar = styled.div`
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const ProgressFill = styled.div<{ percent: number }>`
  height: 100%;
  width: ${props => props.percent}%;
  background: linear-gradient(90deg, #3b82f6 0%, #22c55e 100%);
  transition: width 0.3s ease;
`;

const TaskList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
`;

const TaskItem = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${props => {
    switch (props.status) {
      case 'COMPLETED': return '#22c55e';
      case 'IN_PROGRESS': return '#3b82f6';
      case 'BLOCKED': return '#ef4444';
      case 'SKIPPED': return '#64748b';
      default: return '#94a3b8';
    }
  }};

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const TaskIcon = styled.span<{ status: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;

  ${props => {
    switch (props.status) {
      case 'COMPLETED': return 'background: rgba(34, 197, 94, 0.2);';
      case 'IN_PROGRESS': return 'background: rgba(59, 130, 246, 0.2);';
      case 'BLOCKED': return 'background: rgba(239, 68, 68, 0.2);';
      case 'SKIPPED': return 'background: rgba(100, 116, 139, 0.2);';
      default: return 'background: rgba(148, 163, 184, 0.2);';
    }
  }}
`;

const ClientStats = styled.div`
  display: flex;
  gap: 2rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 1rem;
`;

const ClientStat = styled.div``;

const ClientStatValue = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
`;

const ClientStatLabel = styled.div`
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.5rem;
  cursor: pointer;
  &:hover { color: #ffffff; }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #e2e8f0;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }

  option {
    background: #1e293b;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const DetailPanel = styled.div`
  background: #0f172a;
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const TaskCategory = styled.div`
  margin-bottom: 1.5rem;
`;

const CategoryHeader = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
`;

const TaskDetails = styled.div`
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  margin-top: 0.5rem;
`;

const AIActionsSection = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 12px;
  margin-bottom: 1.5rem;
`;

const AIActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const AIBadge = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.65rem;
  text-transform: uppercase;
`;

const AILabel = styled.div`
  font-size: 0.75rem;
  color: #a78bfa;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const formatCurrency = (value: number) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

interface ClientOnboardingScreenProps {
  embedded?: boolean;
  onComplete?: () => void;
  triggerNew?: number; // Increment to trigger new client modal
}

const ClientOnboardingScreen: React.FC<ClientOnboardingScreenProps> = ({ embedded = false, onComplete, triggerNew }) => {
  const [clients, setClients] = useState<OnboardingClient[]>([]);
  const [stats, setStats] = useState<OnboardingStats | null>(null);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<OnboardingClient | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // AI Features modals
  const [showRiskAssessment, setShowRiskAssessment] = useState(false);
  const [showDocumentGenerator, setShowDocumentGenerator] = useState(false);
  const [aiClientId, setAiClientId] = useState<string | null>(null);
  const [aiClientName, setAiClientName] = useState<string>('');

  // Trigger new client modal when triggerNew changes
  useEffect(() => {
    if (triggerNew && triggerNew > 0) {
      setShowNewClientModal(true);
    }
  }, [triggerNew]);

  // Form state for new client
  const [formData, setFormData] = useState({
    householdName: '',
    primaryContactName: '',
    email: '',
    phone: '',
    estimatedAum: '',
    advisorId: '',
  });

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get('/api/client-onboarding');
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching onboarding clients:', error);
      setClients([]);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/client-onboarding/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching onboarding stats:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchClients(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchClients, fetchStats]);

  const handleNewClient = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await axios.post('/api/client-onboarding', {
        householdName: formData.householdName,
        primaryContactName: formData.primaryContactName,
        email: formData.email,
        phone: formData.phone,
        estimatedAum: formData.estimatedAum ? parseFloat(formData.estimatedAum) : 0,
        advisorId: formData.advisorId ? parseInt(formData.advisorId) : null,
      });

      setClients([response.data, ...clients]);
      setShowNewClientModal(false);
      setFormData({
        householdName: '',
        primaryContactName: '',
        email: '',
        phone: '',
        estimatedAum: '',
        advisorId: '',
      });
      await fetchStats();
      window.alert('Client onboarding started successfully!');
    } catch (error) {
      console.error('Error creating onboarding:', error);
      window.alert('Failed to start onboarding. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId: string, status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'BLOCKED' | 'SKIPPED', blockedReason?: string) => {
    try {
      const response = await axios.put(`/api/client-onboarding/tasks/${taskId}/status`, {
        status,
        blockedReason,
      });

      // Update the task in the client list
      const updatedTask = response.data;
      setClients(clients.map(client => {
        if (client.onboardingId === selectedClient?.onboardingId) {
          return {
            ...client,
            tasks: client.tasks.map(t => t.taskId === taskId ? updatedTask : t),
          };
        }
        return client;
      }));

      // Refresh the selected client
      if (selectedClient) {
        const refreshed = await axios.get(`/api/client-onboarding/${selectedClient.onboardingId}`);
        setSelectedClient(refreshed.data);
      }

      await fetchStats();
    } catch (error) {
      console.error('Error updating task status:', error);
      window.alert('Failed to update task. Please try again.');
    }
  };

  const handleCompleteOnboarding = async (onboardingId: string) => {
    if (!window.confirm('Are you sure you want to complete this onboarding and create the household?')) {
      return;
    }

    try {
      const response = await axios.post(`/api/client-onboarding/${onboardingId}/complete`);
      setClients(clients.map(c => c.onboardingId === onboardingId ? response.data : c));
      setSelectedClient(null);
      await fetchStats();
      window.alert('Onboarding completed! Household has been created.');
      if (onComplete) onComplete();
    } catch (error: any) {
      console.error('Error completing onboarding:', error);
      const message = error.response?.data?.message || 'Failed to complete onboarding. Make sure all required tasks are done.';
      window.alert(message);
    }
  };

  const handleCancelOnboarding = async (onboardingId: string) => {
    const reason = window.prompt('Enter reason for cancellation:');
    if (!reason) return;

    try {
      await axios.post(`/api/client-onboarding/${onboardingId}/cancel?reason=${encodeURIComponent(reason)}`);
      await fetchClients();
      await fetchStats();
      setSelectedClient(null);
      window.alert('Onboarding cancelled.');
    } catch (error) {
      console.error('Error cancelling onboarding:', error);
      window.alert('Failed to cancel onboarding. Please try again.');
    }
  };

  const getStageCounts = () => {
    if (stats) {
      return {
        PROSPECT: stats.prospectCount,
        DOCUMENTS: stats.documentsCount,
        ACCOUNTS: stats.accountsCount,
        FUNDING: stats.fundingCount,
        COMPLETE: stats.completeCount,
      };
    }
    const counts: Record<string, number> = {
      PROSPECT: 0,
      DOCUMENTS: 0,
      ACCOUNTS: 0,
      FUNDING: 0,
      COMPLETE: 0,
    };
    clients.forEach(c => {
      counts[c.stage] = (counts[c.stage] || 0) + 1;
    });
    return counts;
  };

  const stageCounts = getStageCounts();
  const totalEstimatedAum = clients.reduce((sum, c) => sum + (c.estimatedAum || 0), 0);
  const avgProgress = clients.length > 0
    ? Math.round(clients.reduce((sum, c) => sum + (c.progressPercent || 0), 0) / clients.length)
    : 0;

  const filteredClients = selectedStage
    ? clients.filter(c => c.stage === selectedStage)
    : clients;

  const getTaskIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED': return '✓';
      case 'IN_PROGRESS': return '•';
      case 'BLOCKED': return '!';
      case 'SKIPPED': return '–';
      default: return '○';
    }
  };

  const groupTasksByCategory = (tasks: OnboardingTask[]) => {
    const grouped: Record<string, OnboardingTask[]> = {};
    tasks.forEach(task => {
      if (!grouped[task.taskCategory]) {
        grouped[task.taskCategory] = [];
      }
      grouped[task.taskCategory].push(task);
    });
    return grouped;
  };

  const openRiskAssessment = (client: OnboardingClient) => {
    setAiClientId(client.onboardingId);
    setAiClientName(client.householdName);
    setShowRiskAssessment(true);
  };

  const openDocumentGenerator = (client: OnboardingClient) => {
    setAiClientId(client.onboardingId);
    setAiClientName(client.householdName);
    setShowDocumentGenerator(true);
  };

  const handleRiskAssessmentComplete = async (riskProfile: string) => {
    setShowRiskAssessment(false);
    // Refresh the client to get updated risk profile
    if (selectedClient) {
      try {
        const refreshed = await axios.get(`/api/client-onboarding/${selectedClient.onboardingId}`);
        setSelectedClient(refreshed.data);
        setClients(clients.map(c => c.onboardingId === selectedClient.onboardingId ? refreshed.data : c));
      } catch (error) {
        console.error('Error refreshing client:', error);
      }
    }
  };

  if (loading) {
    return (
      <Container embedded={embedded}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>
          Loading onboarding data...
        </div>
      </Container>
    );
  }

  return (
    <Container embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>Client Onboarding</Title>
          <Button variant="primary" onClick={() => setShowNewClientModal(true)}>+ Start Onboarding</Button>
        </Header>
      )}

      <StatsGrid>
        <StatCard>
          <StatValue>{stats?.totalActive || clients.filter(c => c.stage !== 'COMPLETE').length}</StatValue>
          <StatLabel>In Pipeline</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(totalEstimatedAum)}</StatValue>
          <StatLabel>Estimated AUM</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{avgProgress}%</StatValue>
          <StatLabel>Avg. Progress</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats?.overdueTasksCount || 0}</StatValue>
          <StatLabel>Overdue Tasks</StatLabel>
        </StatCard>
      </StatsGrid>

      <Pipeline>
        {['PROSPECT', 'DOCUMENTS', 'ACCOUNTS', 'FUNDING', 'COMPLETE'].map((stage) => (
          <PipelineStage
            key={stage}
            active={selectedStage === stage}
            onClick={() => setSelectedStage(selectedStage === stage ? null : stage)}
          >
            <StageLabel>{stage}</StageLabel>
            <StageCount>{stageCounts[stage as keyof typeof stageCounts] || 0}</StageCount>
          </PipelineStage>
        ))}
      </Pipeline>

      {filteredClients.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>
          {selectedStage
            ? `No clients in ${selectedStage} stage`
            : 'No onboarding clients yet. Click "Start Onboarding" to begin.'}
        </div>
      ) : (
        <ClientList>
          {filteredClients.map((client) => (
            <ClientCard key={client.onboardingId}>
              <ClientHeader>
                <ClientInfo>
                  <ClientName>{client.householdName}</ClientName>
                  <ClientContact>
                    {client.primaryContactName} | {client.primaryEmail || 'No email'} | {client.primaryPhone || 'No phone'}
                  </ClientContact>
                </ClientInfo>
                <ClientMeta>
                  <StageBadge stage={client.stage}>{client.stage}</StageBadge>
                  <Button
                    variant="secondary"
                    style={{ padding: '0.5rem 1rem' }}
                    onClick={() => setSelectedClient(selectedClient?.onboardingId === client.onboardingId ? null : client)}
                  >
                    {selectedClient?.onboardingId === client.onboardingId ? 'Hide Details' : 'View Details'}
                  </Button>
                </ClientMeta>
              </ClientHeader>

              <ProgressBar>
                <ProgressFill percent={client.progressPercent || 0} />
              </ProgressBar>

              <TaskList>
                {client.tasks?.slice(0, 6).map((task) => (
                  <TaskItem
                    key={task.taskId}
                    status={task.status}
                    onClick={() => setSelectedClient(client)}
                  >
                    <TaskIcon status={task.status}>{getTaskIcon(task.status)}</TaskIcon>
                    {task.taskName}
                  </TaskItem>
                ))}
                {client.tasks?.length > 6 && (
                  <TaskItem status="PENDING" onClick={() => setSelectedClient(client)}>
                    +{client.tasks.length - 6} more tasks
                  </TaskItem>
                )}
              </TaskList>

              <ClientStats>
                <ClientStat>
                  <ClientStatValue>{formatCurrency(client.estimatedAum || 0)}</ClientStatValue>
                  <ClientStatLabel>Est. AUM</ClientStatLabel>
                </ClientStat>
                <ClientStat>
                  <ClientStatValue>{client.riskProfile || 'Not Set'}</ClientStatValue>
                  <ClientStatLabel>Risk Profile</ClientStatLabel>
                </ClientStat>
                <ClientStat>
                  <ClientStatValue>{client.assignedAdvisor?.fullName || client.assignedAdvisor?.username || 'Unassigned'}</ClientStatValue>
                  <ClientStatLabel>Advisor</ClientStatLabel>
                </ClientStat>
                <ClientStat>
                  <ClientStatValue>{new Date(client.startedAt || client.createdAt).toLocaleDateString()}</ClientStatValue>
                  <ClientStatLabel>Started</ClientStatLabel>
                </ClientStat>
              </ClientStats>

              {selectedClient?.onboardingId === client.onboardingId && (
                <DetailPanel>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                    <h4 style={{ color: '#ffffff', margin: 0 }}>Task Details</h4>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {client.stage !== 'COMPLETE' && (
                        <>
                          <Button
                            variant="primary"
                            onClick={() => handleCompleteOnboarding(client.onboardingId)}
                            disabled={client.tasks.some(t => t.isRequired && t.status !== 'COMPLETED' && t.status !== 'SKIPPED')}
                          >
                            Complete Onboarding
                          </Button>
                          <Button variant="danger" onClick={() => handleCancelOnboarding(client.onboardingId)}>
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* AI-Powered Actions */}
                  <AIActionsSection>
                    <div style={{ flex: 1 }}>
                      <AILabel>
                        <span>✨</span> AI-Powered Actions
                        <AIBadge>New</AIBadge>
                      </AILabel>
                      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <AIActionButton onClick={() => openRiskAssessment(client)}>
                          <span>📊</span>
                          Risk Assessment
                        </AIActionButton>
                        <AIActionButton onClick={() => openDocumentGenerator(client)}>
                          <span>📄</span>
                          Generate Documents
                        </AIActionButton>
                      </div>
                    </div>
                  </AIActionsSection>

                  {Object.entries(groupTasksByCategory(client.tasks)).map(([category, tasks]) => (
                    <TaskCategory key={category}>
                      <CategoryHeader>{category.replace('_', ' ')}</CategoryHeader>
                      {tasks.map((task) => (
                        <TaskDetails key={task.taskId}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <TaskIcon status={task.status}>{getTaskIcon(task.status)}</TaskIcon>
                              <span style={{ color: '#ffffff' }}>{task.taskName}</span>
                              {task.isRequired && (
                                <span style={{ fontSize: '0.65rem', color: '#ef4444' }}>*Required</span>
                              )}
                            </div>
                            {client.stage !== 'COMPLETE' && (
                              <Select
                                value={task.status}
                                onChange={(e) => handleUpdateTaskStatus(task.taskId, e.target.value as any)}
                                style={{ width: 'auto', padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                              >
                                <option value="PENDING">Pending</option>
                                <option value="IN_PROGRESS">In Progress</option>
                                <option value="COMPLETED">Completed</option>
                                <option value="BLOCKED">Blocked</option>
                                <option value="SKIPPED">Skipped</option>
                              </Select>
                            )}
                          </div>
                          {task.description && (
                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem' }}>
                              {task.description}
                            </div>
                          )}
                          {task.blockedReason && (
                            <div style={{ fontSize: '0.75rem', color: '#ef4444', marginTop: '0.25rem' }}>
                              Blocked: {task.blockedReason}
                            </div>
                          )}
                        </TaskDetails>
                      ))}
                    </TaskCategory>
                  ))}
                </DetailPanel>
              )}
            </ClientCard>
          ))}
        </ClientList>
      )}

      {/* New Client Modal */}
      {showNewClientModal && (
        <ModalOverlay onClick={() => setShowNewClientModal(false)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Start New Onboarding</ModalTitle>
              <CloseButton onClick={() => setShowNewClientModal(false)}>&times;</CloseButton>
            </ModalHeader>

            <form onSubmit={handleNewClient}>
              <FormGroup>
                <Label>Household Name *</Label>
                <Input
                  type="text"
                  value={formData.householdName}
                  onChange={e => setFormData({ ...formData, householdName: e.target.value })}
                  required
                  placeholder="e.g., Smith Family Trust"
                />
              </FormGroup>

              <FormGroup>
                <Label>Primary Contact Name *</Label>
                <Input
                  type="text"
                  value={formData.primaryContactName}
                  onChange={e => setFormData({ ...formData, primaryContactName: e.target.value })}
                  required
                  placeholder="e.g., John Smith"
                />
              </FormGroup>

              <FormGroup>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder="e.g., john@example.com"
                />
              </FormGroup>

              <FormGroup>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="e.g., (555) 123-4567"
                />
              </FormGroup>

              <FormGroup>
                <Label>Estimated AUM ($)</Label>
                <Input
                  type="number"
                  value={formData.estimatedAum}
                  onChange={e => setFormData({ ...formData, estimatedAum: e.target.value })}
                  placeholder="e.g., 1500000"
                />
              </FormGroup>

              <ButtonGroup>
                <Button type="button" variant="secondary" onClick={() => setShowNewClientModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? 'Starting...' : 'Start Onboarding'}
                </Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* AI Risk Assessment Modal */}
      {showRiskAssessment && aiClientId && (
        <RiskAssessmentModal
          onboardingId={aiClientId}
          clientName={aiClientName}
          onClose={() => setShowRiskAssessment(false)}
          onComplete={handleRiskAssessmentComplete}
        />
      )}

      {/* AI Document Generator Modal */}
      {showDocumentGenerator && aiClientId && (
        <DocumentGeneratorModal
          onboardingId={aiClientId}
          clientName={aiClientName}
          onClose={() => setShowDocumentGenerator(false)}
        />
      )}
    </Container>
  );
};

export default ClientOnboardingScreen;
